import type { AdditionalInformationSection } from '~/components/types/incident-configuration-types';

// Single source for the section cap — validators and UI parameterize on this, never the literal.
export const ADDITIONAL_INFORMATION_MAX_SECTIONS = 10;
// Section id/key length cap — mirrors the backend StringConstraints(max_length=64) on the section id.
export const SECTION_KEY_MAX_LENGTH = 64;

// Formik path to the custom-section list (declared on the shared incident-details schema, every LOB).
export const ADDITIONAL_INFORMATION_SECTIONS_PATH = 'configuration.incident_details.additional_information_sections';
export const CONFIGURED_FIELDS_PATH = 'configuration.incident_details.configured_fields';

// A section id is at least one alphanumeric, then a-z / 0-9 / _ (the required leading alphanumeric
// rejects an all-underscore key). Mirrors the backend rule so the two cannot drift.
//
// There used to be a mandatory `fscx_` prefix here as well, and it carried a second job: no built-in
// section id started with it, so a custom id could never collide with one and steal its `sectionRefs`
// anchor. Clients name their own sections now, so that guarantee is gone with the prefix, and the
// reserved-id list below is what replaces it. Both sides enforce it: this list and the backend's
// `_build_reserved_section_ids`, which carries a copy of these panel ids because it cannot reflect on
// them.
// Section ids already owned by a built-in section, and so not claimable as a custom section id.
//
// This exists because the mandatory `fscx_` prefix went away. No built-in id started with it, so a
// custom id could never collide with one; that is what made a reserved list unnecessary, and removing
// the prefix without restoring the list would have made "Documents" derive `documents` and quietly
// share a scroll-anchor and a React key with the built-in Files section (`FsIndexPanel` keys both by
// bare section id, last write wins).
//
// A written-down list rots, so `sectionIdDrift.test.tsx` renders the real config screen, captures the
// section tree it hands the index panel, and asserts every id in it appears here. Add a built-in
// section to the screen without adding it here and that test reds.
export const RESERVED_SECTION_IDS = new Set<string>([
  // Observed in the rendered screen by the drift test.
  'general_details',
  'incident_types',
  'incident_details',
  'involved_parties',
  'additional_information',
  'supplementary_information',
  'witnesses',
  'external_claim_numbers',
  'triage',
  'documents',
  // The Add-Section affordance's own panel id. Feature-gated, so the drift test's fixture does not
  // render it -- listed from the source (`LobSpecificSections.tsx`) rather than observed.
  'add_section_panel',
  // Auto-LOB sections. On an auto config a custom section shares the band, the panel's ref map and the
  // React keys with these, and they are reserved for every LOB so the frontend and the backend reject
  // the same ids rather than the user discovering the difference through a 400.
  'auto_incident_additional',
  'police_and_fire_department',
  'theft_follow_up',
  'fault_assessment',
  'accident_follow_up',
  // Auto-LOB involved-party panels (`FnolConfigurationScreen.jsx` lists them by hand); they sit in the
  // same panel namespace as a custom section on an auto config. The drift test's auto arm keeps this in
  // step with what the screen renders.
  'involved_vehicle',
  'involved_driver',
  'involved_passenger',
  'involved_non_motorist',
  'involved_generic_property',
  // Runtime ids that share a custom section's namespace off the config screen: the auto FNOL form's
  // built-in row ids (`verified_<id>` / `checked_<id>` Formik fields), the auto More Details menu entry
  // id, and the insured-info verified flag. Walked by reservedRuntimeIds.test.ts.
  'initial_fault_assessment',
  'initial_theft_follow_up',
  'initial_accident_follow_up',
  'police_and_fire_department_involvement',
  'insured_info',
  // Structural `configured_field.section` markers that are not themselves rendered panel ids. Both are
  // live in stored configs (`covered_vehicle` in US production), so a custom section must not be able to
  // claim either and shadow them.
  'supplementary_info',
  'covered_vehicle',
]);

export const SECTION_KEY_PATTERN = /^[a-z0-9][a-z0-9_]*$/;

// When a Display Name yields no valid ASCII key even after stripping disallowed characters (a fully
// non-Latin name like Hebrew/Cyrillic, or whitespace/punctuation only), the Key auto-derivation has
// nothing to use. Fall back to a unique `section_<n>` machine key (still editable until save) so the id
// stays ASCII everywhere (API/CR/JSONB) -- no transliteration.
const SECTION_KEY_FALLBACK_PREFIX = 'section_';

export const nextSectionFallbackKey = (existingKeys: string[]): string => {
  const taken = new Set(existingKeys);
  let n = 1;
  while (taken.has(`${SECTION_KEY_FALLBACK_PREFIX}${n}`)) n += 1;
  return `${SECTION_KEY_FALLBACK_PREFIX}${n}`;
};

// The section cap counts only *shown* sections: a section with "Show" off is never rendered on the FNOL
// form, so it must not consume a slot. Absent `active` counts as shown — the backend section schema
// declares `active: bool = True` and its hidden-section guard tests `active is False`, the same form used
// here. Single source for the rule so the ADD-SECTION gate and the save-time yup check cannot drift.
export const countActiveSections = (sections?: readonly AdditionalInformationSection[] | null): number =>
  (sections ?? []).filter((section) => section?.active !== false).length;
